/**
 * Where a deal stands, in the words a banker reads it in (DOP-615).
 *
 * The Deals home shows one status and one next step per deal, so both come
 * from the same function: a row can never say "All clear" and offer
 * "Review", and the summary tiles count exactly the rows that say the thing
 * the tile names.
 *
 * "To review" counts conflicts, gaps and sections left out of the memo, the
 * same items the Needs you tab lists. Blocking fields are gaps too, so adding
 * `blocking_open` would count one missing field twice.
 *
 * A section set to appear in every memo that is still to write (DOP-659) is
 * one of the sections left out, so it is already in `sections_omitted`. The
 * summary cannot say which omitted sections are required; the full run can,
 * and the workflow page passes that count in as `sectionsToWrite` once it
 * has loaded. The larger of the two is counted, never their sum, so a memo
 * whose only open item is a required section reads "1 thing needs you",
 * never "All clear". While the full run is still loading, a summary that
 * shows omitted sections already reads "needs you" with that count: the
 * honest state until the run says whether any of them blocks.
 *
 * A section the run wrote and a person then emptied in edit mode is required
 * too, and no summary count shows it: the workflow page reads every draft's
 * full run for that, and until it answers such a row reads from its summary.
 */

import type { DealScreeningRunSummary } from "../../types/dealScreening";

export type DealStandingKind =
	| "not_run"
	| "writing"
	| "failed"
	| "needs_you"
	| "ready"
	| "reviewed";

export interface DealStanding {
	kind: DealStandingKind;
	/** The status in plain words. */
	label: string;
	/** Things on the latest memo a person has to look at. */
	openCount: number;
}

export const openCount = (run: DealScreeningRunSummary | null, sectionsToWrite = 0): number =>
	run && !run.generating
		? run.conflicts_open +
			run.gaps_open +
			Math.max(run.counts.sections_omitted ?? 0, sectionsToWrite)
		: 0;

export function dealStanding(
	run: DealScreeningRunSummary | null,
	/** Required sections still to write, from the full run when it has loaded. */
	sectionsToWrite = 0,
): DealStanding {
	if (!run) return { kind: "not_run", label: "Not screened yet", openCount: 0 };
	if (run.generating) {
		return { kind: "writing", label: "Running…", openCount: 0 };
	}
	if (run.generation_error) {
		return { kind: "failed", label: "Couldn't finish the memo", openCount: 0 };
	}
	const open = openCount(run, sectionsToWrite);
	if (run.status === "reviewed") {
		return { kind: "reviewed", label: `Reviewed · v${run.version}`, openCount: open };
	}
	if (open > 0) {
		return {
			kind: "needs_you",
			label: open === 1 ? "1 gap open" : `${open} gaps open`,
			openCount: open,
		};
	}
	return { kind: "ready", label: "All clear", openCount: 0 };
}
